/**
 * AgentPage — the /agent AI-assistant workspace (FR-015).
 *
 * Claude/ChatGPT-style full-page surface over the existing KI-Hofassistent:
 * a lens-style left panel manages PROJECTS (level 1) and CONVERSATIONS
 * (level 2) with create/rename/move/delete, the main pane renders the shared
 * ``AssistantConversation`` (the same implementation as the side-panel KI
 * tab — markdown/tables, budget, async-202 lifecycle, retry, sources).
 *
 * State model (mirrors /farm): the URL is the single source of truth —
 * ``?chat=<thread_id>`` selects a conversation, ``?project=<project_id>``
 * holds the "new conversation inside this project" context. Conversations are
 * the user's ``agent_assistant`` chat threads (listed via the existing
 * ``/chat/threads``); a first message on the empty state CREATES the
 * conversation (``ensureThreadId``) so no empty threads pile up.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  Folder,
  FolderInput,
  FolderMinus,
  FolderPlus,
  MessageSquarePlus,
  MessagesSquare,
  Pencil,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  Button,
  ConfirmationModal,
  LensePanel,
  type LenseItem,
  type LenseItemAction,
  type LenseSubItem,
} from '@hofsync/design-system';
import { useChatThreads } from '../../hooks/useChatQueries';
import {
  useAssistantProjects,
  useCreateAssistantConversation,
  useCreateAssistantProject,
  useDeleteAssistantConversation,
  useDeleteAssistantProject,
  useRenameAssistantProject,
  useUpdateAssistantConversation,
} from '../../hooks/useAssistantQueries';
import { AssistantConversation } from '../../components/chat/AssistantConversation';
import { NameDialog } from './NameDialog';
import type { ChatThread } from '../../services/chatApi';
import './AgentPage.css';

/** Fixed level-1 id for the ungrouped-conversations bucket. Cannot collide
 * with a project id (those are UUIDs). */
const UNGROUPED_ID = 'conversations';

type DialogState =
  | { kind: 'create-project' }
  | { kind: 'rename-project'; projectId: string; current: string }
  | { kind: 'rename-conversation'; threadId: string; current: string }
  | null;

type ConfirmState =
  | { kind: 'project'; projectId: string; name: string; count: number }
  | { kind: 'conversation'; threadId: string; title: string }
  | null;

/** Compact relative recency for conversation rows ("5 Min.", "3 Std.", "2 Tg."). */
function relativeRecency(iso: string | null | undefined, locale: string): string | undefined {
  if (!iso) return undefined;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return undefined;
  const deltaS = Math.max(0, (Date.now() - then) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'narrow' });
  if (deltaS < 3600) return rtf.format(-Math.max(1, Math.round(deltaS / 60)), 'minute');
  if (deltaS < 86400) return rtf.format(-Math.round(deltaS / 3600), 'hour');
  return rtf.format(-Math.round(deltaS / 86400), 'day');
}

export const AgentPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatParam = searchParams.get('chat');
  const projectParam = searchParams.get('project');

  const threadsQuery = useChatThreads();
  const projectsQuery = useAssistantProjects();
  const createProject = useCreateAssistantProject();
  const renameProject = useRenameAssistantProject();
  const deleteProject = useDeleteAssistantProject();
  const createConversation = useCreateAssistantConversation();
  const updateConversation = useUpdateAssistantConversation();
  const deleteConversation = useDeleteAssistantConversation();

  const [dialog, setDialog] = useState<DialogState>(null);
  const [confirmDelete, setConfirmDelete] = useState<ConfirmState>(null);

  const conversations = useMemo(
    () => (threadsQuery.data ?? []).filter((tr) => tr.thread_kind === 'agent_assistant'),
    [threadsQuery.data],
  );
  const projects = projectsQuery.data ?? [];
  const selectedThread = chatParam
    ? conversations.find((c) => c.thread_id === chatParam)
    : undefined;

  // "New conversation in which project" context: the open conversation's
  // project wins, else the project selected in the panel.
  const projectContext = selectedThread ? (selectedThread.project_id ?? null) : projectParam;

  const untitled = t('agent.untitledConversation', 'New conversation');
  const conversationName = useCallback(
    (c: ChatThread) => (c.title && c.title.trim() ? c.title : untitled),
    [untitled],
  );

  // ------------------------------------------------------------------ //
  // Selection → URL (the /farm pattern; deep-linkable, refresh-stable).
  // ------------------------------------------------------------------ //
  const openConversation = useCallback(
    (threadId: string) => setSearchParams({ chat: threadId }, { replace: false }),
    [setSearchParams],
  );
  const openNewChat = useCallback(
    (projectId?: string | null) =>
      setSearchParams(projectId ? { project: projectId } : {}, { replace: false }),
    [setSearchParams],
  );

  const handlePanelSelect = (itemId: string, subItemId: string | null) => {
    if (subItemId) {
      openConversation(subItemId);
    } else if (itemId === UNGROUPED_ID) {
      openNewChat(null);
    } else {
      openNewChat(itemId);
    }
  };

  // ------------------------------------------------------------------ //
  // Row actions (rename / move / delete via the LensePanel context menus).
  // ------------------------------------------------------------------ //
  const conversationActions = useCallback(
    (c: ChatThread): LenseItemAction[] => {
      const moveTargets: LenseItemAction[] = projects
        .filter((p) => p.project_id !== c.project_id)
        .map((p) => ({
          id: `move:${p.project_id}`,
          label: t('agent.actions.moveTo', 'Move to "{{name}}"', { name: p.name }),
          icon: FolderInput,
          onSelect: () =>
            updateConversation.mutate({
              threadId: c.thread_id,
              patch: { project_id: p.project_id },
            }),
        }));
      return [
        {
          id: 'rename',
          label: t('agent.actions.rename', 'Rename'),
          icon: Pencil,
          onSelect: () =>
            setDialog({
              kind: 'rename-conversation',
              threadId: c.thread_id,
              current: c.title ?? '',
            }),
        },
        ...moveTargets,
        ...(c.project_id
          ? [
              {
                id: 'unfile',
                label: t('agent.actions.removeFromProject', 'Remove from project'),
                icon: FolderMinus,
                onSelect: () =>
                  updateConversation.mutate({
                    threadId: c.thread_id,
                    patch: { project_id: null },
                  }),
              },
            ]
          : []),
        {
          id: 'delete',
          label: t('agent.actions.delete', 'Delete'),
          icon: Trash2,
          danger: true,
          onSelect: () =>
            setConfirmDelete({
              kind: 'conversation',
              threadId: c.thread_id,
              title: conversationName(c),
            }),
        },
      ];
    },
    [projects, t, updateConversation, conversationName],
  );

  const toSubItem = useCallback(
    (c: ChatThread): LenseSubItem => ({
      id: c.thread_id,
      name: conversationName(c),
      meta: relativeRecency(c.last_message_at, i18n.language),
      actions: conversationActions(c),
    }),
    [conversationName, conversationActions, i18n.language],
  );

  const ungrouped = conversations.filter((c) => !c.project_id);
  const items: LenseItem[] = [
    {
      id: UNGROUPED_ID,
      name: t('agent.conversations', 'Conversations'),
      icon: <MessagesSquare size={16} aria-hidden="true" />,
      count: ungrouped.length > 0 ? ungrouped.length : undefined,
      children: ungrouped.map(toSubItem),
    },
    ...projects.map((p) => {
      const inProject = conversations.filter((c) => c.project_id === p.project_id);
      return {
        id: p.project_id,
        name: p.name,
        icon: <Folder size={16} aria-hidden="true" />,
        count: inProject.length > 0 ? inProject.length : undefined,
        children: inProject.map(toSubItem),
        actions: [
          {
            id: 'rename',
            label: t('agent.actions.rename', 'Rename'),
            icon: Pencil,
            onSelect: () =>
              setDialog({ kind: 'rename-project', projectId: p.project_id, current: p.name }),
          },
          {
            id: 'delete',
            label: t('agent.actions.delete', 'Delete'),
            icon: Trash2,
            danger: true,
            onSelect: () =>
              setConfirmDelete({
                kind: 'project',
                projectId: p.project_id,
                name: p.name,
                count: inProject.length,
              }),
          },
        ] satisfies LenseItemAction[],
      };
    }),
  ];

  const selectedItem = selectedThread
    ? (selectedThread.project_id ?? UNGROUPED_ID)
    : (projectParam ?? UNGROUPED_ID);
  const selectedSubItem = selectedThread ? selectedThread.thread_id : null;

  // ------------------------------------------------------------------ //
  // Conversation pane wiring (create-on-first-message + URL sync).
  // ------------------------------------------------------------------ //
  const createConversationAsync = createConversation.mutateAsync;
  const ensureThreadId = useCallback(async () => {
    const created = await createConversationAsync({
      projectId: projectContext ?? undefined,
    });
    return created.thread_id;
  }, [createConversationAsync, projectContext]);

  const handleTurnAccepted = useCallback(
    (threadId: string) => {
      if (chatParam !== threadId) {
        setSearchParams({ chat: threadId }, { replace: true });
      }
    },
    [chatParam, setSearchParams],
  );

  const handleDeleteConfirm = () => {
    if (!confirmDelete) return;
    if (confirmDelete.kind === 'project') {
      deleteProject.mutate(
        { projectId: confirmDelete.projectId },
        {
          onSuccess: (result) => {
            setConfirmDelete(null);
            // The open conversation may have been cascade-deleted; the
            // project context is gone either way — back to the root state.
            if (
              (chatParam && result.deleted_thread_ids.includes(chatParam)) ||
              projectParam === confirmDelete.projectId
            ) {
              openNewChat(null);
            }
          },
          onError: () => setConfirmDelete(null),
        },
      );
    } else {
      deleteConversation.mutate(
        { threadId: confirmDelete.threadId },
        {
          onSuccess: () => {
            setConfirmDelete(null);
            if (chatParam === confirmDelete.threadId) openNewChat(null);
          },
          onError: () => setConfirmDelete(null),
        },
      );
    }
  };

  const handleDialogSubmit = (name: string) => {
    if (!dialog) return;
    const close = { onSuccess: () => setDialog(null) };
    if (dialog.kind === 'create-project') {
      createProject.mutate({ name }, close);
    } else if (dialog.kind === 'rename-project') {
      renameProject.mutate({ projectId: dialog.projectId, name }, close);
    } else {
      updateConversation.mutate({ threadId: dialog.threadId, patch: { title: name } }, close);
    }
  };

  const dialogLoading =
    createProject.isPending || renameProject.isPending || updateConversation.isPending;

  const panelHeader = (
    <div className="agent-page__panel-actions">
      <Button
        type="button"
        size="sm"
        className="agent-page__new-chat"
        data-testid="agent-new-conversation"
        onClick={() => openNewChat(projectContext)}
      >
        <MessageSquarePlus size={15} aria-hidden="true" />
        {t('agent.newConversation', 'New conversation')}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="agent-page__new-project"
        data-testid="agent-new-project"
        onClick={() => setDialog({ kind: 'create-project' })}
      >
        <FolderPlus size={15} aria-hidden="true" />
        {t('agent.newProject', 'New project')}
      </Button>
    </div>
  );

  const emptyState = (
    <div className="agent-page__empty" data-testid="agent-empty">
      <Sparkles size={28} aria-hidden="true" className="agent-page__empty-icon" />
      <h2 className="agent-page__empty-title">{t('agent.emptyTitle', 'How can I help?')}</h2>
      <p className="agent-page__empty-hint">
        {t(
          'agent.emptyHint',
          'Ask about your records — delivery notes, credit notes, liquidity and more.',
        )}
      </p>
    </div>
  );

  // Key the conversation pane by selection so per-turn local state (thinking /
  // failed markers) never bleeds across conversations; the remount
  // re-derivation inside AssistantConversation restores an in-flight turn.
  const paneKey = chatParam ?? `new:${projectContext ?? 'root'}`;

  const activeProject = projectContext
    ? projects.find((p) => p.project_id === projectContext)
    : undefined;
  const topbarTitle = selectedThread
    ? conversationName(selectedThread)
    : t('agent.newConversation', 'New conversation');

  return (
    <div className="agent-page" data-testid="agent-page">
      <LensePanel
        items={items}
        selectedItem={selectedItem}
        selectedSubItem={selectedSubItem}
        onSelect={handlePanelSelect}
        title={t('agent.title', 'KI-Assistent')}
        headerContent={panelHeader}
        actionsAriaLabel={t('agent.actions.menu', 'Actions')}
        className="agent-page__panel"
      />
      <div className="agent-page__main">
        <AssistantConversation
          key={paneKey}
          thread={selectedThread}
          ensureThreadId={ensureThreadId}
          onTurnAccepted={handleTurnAccepted}
          emptyState={emptyState}
          headerLeading={
            <div className="agent-page__topbar" data-testid="agent-topbar">
              <span className="agent-page__topbar-title">{topbarTitle}</span>
              {activeProject && (
                <span className="agent-page__topbar-project">
                  <Folder size={13} aria-hidden="true" />
                  {activeProject.name}
                </span>
              )}
            </div>
          }
          className="agent-page__conversation"
        />
      </div>

      <NameDialog
        isOpen={dialog !== null}
        title={
          dialog?.kind === 'create-project'
            ? t('agent.dialogs.createProjectTitle', 'New project')
            : dialog?.kind === 'rename-project'
              ? t('agent.dialogs.renameProjectTitle', 'Rename project')
              : t('agent.dialogs.renameConversationTitle', 'Rename conversation')
        }
        label={t('agent.dialogs.nameLabel', 'Name')}
        initialValue={dialog && dialog.kind !== 'create-project' ? dialog.current : ''}
        confirmLabel={
          dialog?.kind === 'create-project'
            ? t('agent.dialogs.create', 'Create')
            : t('agent.dialogs.save', 'Save')
        }
        cancelLabel={t('common.cancel', 'Cancel')}
        loading={dialogLoading}
        onSubmit={handleDialogSubmit}
        onCancel={() => setDialog(null)}
      />

      <ConfirmationModal
        isOpen={confirmDelete !== null}
        title={
          confirmDelete?.kind === 'project'
            ? t('agent.deleteProject.title', 'Delete project?')
            : t('agent.deleteConversation.title', 'Delete conversation?')
        }
        message={
          confirmDelete?.kind === 'project'
            ? t(
                'agent.deleteProject.message',
                '"{{name}}" and the {{count}} conversations inside it will be deleted.',
                { name: confirmDelete.name, count: confirmDelete.count },
              )
            : confirmDelete
              ? t('agent.deleteConversation.message', '"{{title}}" will be deleted.', {
                  title: confirmDelete.title,
                })
              : ''
        }
        confirmLabel={t('agent.deleteConfirm', 'Delete')}
        cancelLabel={t('common.cancel', 'Cancel')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        danger
        loading={deleteProject.isPending || deleteConversation.isPending}
      />
    </div>
  );
};

export default AgentPage;
